import React, { useEffect, useState } from 'react'
import { IsGranted, useAuth } from '../../auth'
import { matchingCapabilities } from '../../matching/core/_models'
import { formatMskDateTime } from '../../matching/routes/Dashboard/core/mskTime'
import { getPayoutFlowControlDecisions } from '../../ticket-card/core/refusalApi'
import { PayoutFlowControlDecision } from '../../ticket-card/core/refusal.types'

/**
 * What matching decided about this payout at the precheck and on entry. Shown only when matching
 * decided at all; a refusal on entry otherwise appears on this card only as its close reason.
 */
const MatchingDecisionBlock: React.FC<{ token: string }> = ({ token }) => {
  const { currentUser } = useAuth()
  const allowed = IsGranted(matchingCapabilities.canViewPayoutRefusals, currentUser)
  const [decisions, setDecisions] = useState<PayoutFlowControlDecision[]>([])

  useEffect(() => {
    if (!allowed) return
    let cancelled = false
    getPayoutFlowControlDecisions(token)
      .then(({ data }) => {
        if (!cancelled && data.success) setDecisions(data.data ?? [])
      })
      .catch(() => {
        if (!cancelled) setDecisions([])
      })
    return () => {
      cancelled = true
    }
  }, [token, allowed])

  if (!allowed || decisions.length === 0) return null

  return (
    <div className='card mb-5'>
      <div className='card-header'>
        <h3 className='card-title fw-bolder'>Решение мэтчинга</h3>
      </div>
      <div className='card-body d-flex flex-column' style={{ gap: 16 }}>
        {decisions.map((d) => (
          <Decision key={`${d.at}-${d.stage}`} decision={d} />
        ))}
      </div>
    </div>
  )
}

const BADGE: Record<PayoutFlowControlDecision['verdict'], string> = {
  ACCEPTED: 'badge badge-light-success',
  REFUSED: 'badge badge-light-danger',
  OVERRIDDEN: 'badge badge-light-warning',
}

/** Одно решение: вердикт, стадия и время, причина и сводка; разбор — по клику, он длинный. */
const Decision: React.FC<{ decision: PayoutFlowControlDecision }> = ({ decision: d }) => {
  const [open, setOpen] = useState(false)
  // Причина уже стоит заголовком решения — в разборе её не повторяем.
  const pairs = d.breakdown.filter((p) => p.value !== d.reasonTitle)

  return (
    <div className='d-flex flex-column' style={{ gap: 4 }}>
      <div className='d-flex align-items-center flex-wrap' style={{ columnGap: 8, rowGap: 4 }}>
        <span className={`${BADGE[d.verdict]} text-nowrap`}>{d.verdictTitle}</span>
        <span className='text-muted fs-7 text-nowrap'>
          {d.stageTitle} · {formatMskDateTime(d.at)}
        </span>
      </div>
      <div className='fw-bold'>{d.reasonTitle}</div>
      {d.summary !== d.reasonTitle && <div className='fs-7 text-gray-700'>{d.summary}</div>}
      {pairs.length > 0 && (
        <>
          <button type='button' className='btn btn-link btn-sm p-0 fs-7 align-self-start' onClick={() => setOpen((o) => !o)}>
            {open ? 'Скрыть разбор' : 'Разбор решения'}
          </button>
          {open && (
            <div className='d-flex flex-column' style={{ gap: 6 }}>
              {pairs.map((p) => (
                <div key={p.label} className='d-flex fs-7' style={{ gap: 10 }}>
                  <div className='text-muted' style={{ width: 170, flexShrink: 0 }}>
                    {p.label}
                  </div>
                  <div style={{ minWidth: 0, wordBreak: 'break-word' }}>{p.value}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default MatchingDecisionBlock
